import { Tag, Progress, Empty } from "antd"
import type { StockExecutive, StockHolder } from '@/types/stockPool'
import './index.scss'

/** 职位关键词 → 是否属于核心管理层（董事长 / CEO / 总经理 等） */
function isMajorPosition(position: string): boolean {
    const p = position.toUpperCase()
    return p.includes('CEO') ||
        p.includes('董事长') ||
        p.includes('总经理') ||
        p.includes('PRESIDENT') ||
        p.includes('CHAIRMAN')
}

/** 职位关键词 → 是否属于董事会成员（含董事，排除独立董事归到独立标识） */
function isBoardMember(position: string): boolean {
    const p = position.toUpperCase()
    return p.includes('董事') ||
        p.includes('DIRECTOR')
}

/** 是否独立董事 */
function isIndependent(position: string): boolean {
    return position.includes('独立') || position.toUpperCase().includes('INDEPENDENT')
}

/** 展示用姓名 */
function displayName(exec: StockExecutive): string {
    return exec.leader_name
}

export default function GovernanceStructure({
    executives,
    holders,
}: {
    executives: StockExecutive[]
    holders: StockHolder[]
}) {
    // 主要管理层：董事长 / CEO / 总经理 等核心职位，去重
    const majorSeen = new Set<string>()
    const major = executives
        .filter(e => isMajorPosition(e.position_name))
        .filter(e => {
            const key = `${e.position_name}-${displayName(e)}`
            if (majorSeen.has(key)) return false
            majorSeen.add(key)
            return true
        })
        .map(e => ({ name: displayName(e), label: e.position_name }))

    // 董事会成员：含"董事"的职位，去重
    const boardSeen = new Set<string>()
    const board = executives
        .filter(e => isBoardMember(e.position_name))
        .filter(e => {
            const key = `${e.position_name}-${displayName(e)}`
            if (boardSeen.has(key)) return false
            boardSeen.add(key)
            return true
        })
        .map(e => ({
            name: displayName(e),
            label: e.position_name,
            isIndependent: isIndependent(e.position_name),
        }))

    // 前十大股东：直接映射，持股比例用于进度条
    const ten = holders
        .filter(h => h.holder_name)
        .map(h => ({
            name: h.holder_name as string,
            value: h.holder_pct ?? 0,
            type: h.source_group_name ?? '--',
        }))

    return (
        <div className="governance-structure">
            <div className="governance-structure__body">
                <div className="governance-structure__item governance-structure__major">
                    <div className="governance-structure-title">主要管理层</div>
                    <div className="governance-structure__major-item">
                        {major.length > 0 ? major.map((item) => (
                            <div key={item.label} className="governance-structure-item-row">
                                <span className="governance-structure__major-item-row-label margin-right-8">{item.label}</span>
                                <span className="governance-structure-item-row-name">{item.name}</span>
                            </div>
                        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />}
                    </div>
                    <div className="governance-structure-title">董事会成员</div>
                    <div className="governance-structure__major-item">
                        {board.length > 0 ? board.map((item) => (
                            <div key={`${item.name}-${item.label}`} className="governance-structure-item-row">
                                <span className="governance-structure-item-row-name margin-right-8">{item.name}</span>
                                <span className="governance-structure__major-item-row-label">{item.label}</span>
                                {item.isIndependent && <Tag color="blue" style={{borderRadius: 3,fontSize: 10, background: 'var(--accent-dim)'}}>独立</Tag>}
                            </div>
                        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />}
                    </div>
                </div>
                <div className="governance-structure__item governance-structure__status">
                    <div className="governance-structure-title">近期状态</div>
                    <div className="governance-structure__status-item">
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                    </div>
                </div>
                <div className="governance-structure__item governance-structure__ten">
                    <div className="governance-structure-title">前十大股东持仓</div>
                    <div className="governance-structure__ten-item">
                        {ten.length > 0 ? ten.map((item) => (
                            <div key={item.name} className="governance-structure-item-row governance-structure__ten-item-row">
                                <div className="governance-structure-item-row-name governance-structure__ten-item-name">{item.name}</div>
                                <div className="governance-structure__ten-progress">
                                    <Progress
                                        percent={item.value}
                                        showInfo={false}
                                        size={['100%', 4]}
                                    />
                                </div>
                                <div className="governance-structure__ten-item-value">{item.value}%</div>
                                <div className="governance-structure__ten-item-type text-10">{item.type}</div>
                            </div>
                        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />}
                    </div>
                </div>
            </div>
        </div>
    )
}
